import React, { useEffect, useState } from 'react';
import api, { notifyUpdate } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function AdminTipovi() {
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [typeDialog, setTypeDialog] = useState(false);
  const [catDialog, setCatDialog] = useState(false);
  const [typeForm, setTypeForm] = useState({ name: '' });
  const [catForm, setCatForm] = useState({ name: '', typeId: '' });
  const [editTypeId, setEditTypeId] = useState(null);
  const [editCatId, setEditCatId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [t, c] = await Promise.all([api.get('/types'), api.get('/categories')]);
        setTypes(t.data);
        setCategories(c.data);
      } catch (err) {
        console.error('Greška pri učitavanju:', err);
      }
    };
    load();
    window.addEventListener('store-updated', load);
    return () => window.removeEventListener('store-updated', load);
  }, []);

  const saveType = async () => {
    if (!typeForm.name) { toast.error('Unesite naziv tipa.'); return; }
    try {
      if (editTypeId) {
        await api.put(`/types/${editTypeId}`, typeForm);
        toast.success('Tip turizma je ažuriran.');
      } else {
        await api.post('/types', { ...typeForm, icon: 'Landmark' });
        toast.success('Tip turizma je dodan.');
      }
      notifyUpdate();
      setTypeDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri spremanju.');
    }
  };

  const saveCat = async () => {
    if (!catForm.name || !catForm.typeId) { toast.error('Unesite naziv i odaberite tip.'); return; }
    try {
      if (editCatId) {
        await api.put(`/categories/${editCatId}`, catForm);
        toast.success('Kategorija je ažurirana.');
      } else {
        await api.post('/categories', catForm);
        toast.success('Kategorija je dodana.');
      }
      notifyUpdate();
      setCatDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri spremanju.');
    }
  };

  const removeType = async (id) => {
    try { await api.delete(`/types/${id}`); toast.success('Tip je obrisan.'); notifyUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Greška pri brisanju.'); }
  };

  const removeCat = async (id) => {
    try { await api.delete(`/categories/${id}`); toast.success('Kategorija je obrisana.'); notifyUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Greška pri brisanju.'); }
  };

  return (
    <div data-testid="admin-tipovi">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0b1a3d]">Tipovi i kategorije turizma</h1>
        <p className="text-sm text-slate-500">Upravljanje tipovima turizma (npr. Gastronomija) i kategorijama (npr. Restorani, Aščinice).</p>
      </div>

      <Tabs defaultValue="tipovi">
        <TabsList className="bg-[#e8ecf7] h-10 mb-5">
          <TabsTrigger value="tipovi" data-testid="tab-tipovi" className="data-[state=active]:bg-[#001752] data-[state=active]:text-white px-6">Tipovi turizma</TabsTrigger>
          <TabsTrigger value="kategorije" data-testid="tab-kategorije" className="data-[state=active]:bg-[#001752] data-[state=active]:text-white px-6">Kategorije</TabsTrigger>
        </TabsList>

        <TabsContent value="tipovi">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setTypeForm({ name: '' }); setEditTypeId(null); setTypeDialog(true); }} data-testid="add-type-btn"
              className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
              <Plus className="h-4 w-4 mr-2" /> Novi tip
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#f8f9fd]">
                  <TableHead className="text-[#001752] font-semibold">Naziv tipa</TableHead>
                  <TableHead className="text-[#001752] font-semibold">Broj kategorija</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.map((tp) => (
                  <TableRow key={tp.id} data-testid={`type-row-${tp.id}`}>
                    <TableCell className="font-medium text-slate-700">{tp.name}</TableCell>
                    <TableCell><Badge className="bg-[#e8ecf7] text-[#001752] hover:bg-[#e8ecf7]">{categories.filter((c) => c.typeId === tp.id).length}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1.5 justify-end">
                        <Button size="icon" variant="outline" onClick={() => { setTypeForm({ name: tp.name }); setEditTypeId(tp.id); setTypeDialog(true); }}
                          className="h-8 w-8 border-slate-200 text-[#001752] hover:bg-[#001752] hover:text-white rounded-md transition-colors duration-200" data-testid={`edit-type-${tp.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => removeType(tp.id)}
                          className="h-8 w-8 border-slate-200 text-red-600 hover:bg-red-600 hover:text-white rounded-md transition-colors duration-200" data-testid={`delete-type-${tp.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="kategorije">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setCatForm({ name: '', typeId: '' }); setEditCatId(null); setCatDialog(true); }} data-testid="add-category-btn"
              className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
              <Plus className="h-4 w-4 mr-2" /> Nova kategorija
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#f8f9fd]">
                  <TableHead className="text-[#001752] font-semibold">Naziv kategorije</TableHead>
                  <TableHead className="text-[#001752] font-semibold">Tip turizma</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((c) => (
                  <TableRow key={c.id} data-testid={`category-row-${c.id}`}>
                    <TableCell className="font-medium text-slate-700">{c.name}</TableCell>
                    <TableCell className="text-slate-500">{types.find((tp) => tp.id === c.typeId)?.name || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1.5 justify-end">
                        <Button size="icon" variant="outline" onClick={() => { setCatForm({ name: c.name, typeId: c.typeId }); setEditCatId(c.id); setCatDialog(true); }}
                          className="h-8 w-8 border-slate-200 text-[#001752] hover:bg-[#001752] hover:text-white rounded-md transition-colors duration-200" data-testid={`edit-category-${c.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => removeCat(c.id)}
                          className="h-8 w-8 border-slate-200 text-red-600 hover:bg-red-600 hover:text-white rounded-md transition-colors duration-200" data-testid={`delete-category-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={typeDialog} onOpenChange={setTypeDialog}>
        <DialogContent className="max-w-md" data-testid="type-dialog">
          <DialogHeader><DialogTitle className="text-[#0b1a3d]">{editTypeId ? 'Uredi tip' : 'Novi tip turizma'}</DialogTitle></DialogHeader>
          <div>
            <Label className="text-slate-600 mb-1.5 block">Naziv tipa *</Label>
            <Input value={typeForm.name} onChange={(e) => setTypeForm({ name: e.target.value })} data-testid="type-form-name" className="border-slate-200" placeholder="npr. Noćni život" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialog(false)} className="border-slate-200">Odustani</Button>
            <Button onClick={saveType} className="bg-[#001752] hover:bg-[#0a2a7a] text-white transition-colors duration-200" data-testid="type-form-save">Sačuvaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent className="max-w-md" data-testid="category-dialog">
          <DialogHeader><DialogTitle className="text-[#0b1a3d]">{editCatId ? 'Uredi kategoriju' : 'Nova kategorija'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-600 mb-1.5 block">Naziv kategorije *</Label>
              <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="category-form-name" className="border-slate-200" placeholder="npr. Teniski tereni" />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Tip turizma *</Label>
              <Select value={catForm.typeId} onValueChange={(v) => setCatForm({ ...catForm, typeId: v })}>
                <SelectTrigger className="border-slate-200" data-testid="category-form-type"><SelectValue placeholder="Odaberite tip" /></SelectTrigger>
                <SelectContent>{types.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(false)} className="border-slate-200">Odustani</Button>
            <Button onClick={saveCat} className="bg-[#001752] hover:bg-[#0a2a7a] text-white transition-colors duration-200" data-testid="category-form-save">Sačuvaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
